import React, { useEffect, useState } from 'react';
import { useI18n, translate } from '../i18n';

// Captioning by hand (transcribing dialect, timing, splitting lines) takes
// roughly 8 minutes of work per minute of video.
export const MANUAL_MINUTES_PER_VIDEO_MINUTE = 8;

// Only celebrate once there is something worth showing.
const MIN_COMPLETED_VIDEOS = 3;
const MIN_SAVED_MINUTES = 60;

export const savedMinutesFor = (videoSeconds) => (videoSeconds / 60) * MANUAL_MINUTES_PER_VIDEO_MINUTE;

export const isTimeSavedSignificant = ({ videoSeconds = 0, videos = 0 }) =>
  videos >= MIN_COMPLETED_VIDEOS && savedMinutesFor(videoSeconds) >= MIN_SAVED_MINUTES;

export const formatTimeSaved = (minutes) => {
  const total = Math.round(minutes);
  if (total < 60) return { value: String(total), unit: translate('minutes (unit)', { count: total }) };
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours >= 10 || rest === 0) {
    const rounded = Math.round(total / 60);
    return { value: String(rounded), unit: translate('hours (unit)', { count: rounded }) };
  }
  return { value: translate('{hours}h {minutes}m', { hours, minutes: rest }), unit: '' };
};

// A relatable comparison for the time saved, picked by magnitude.
export const timeSavedEquivalent = (minutes) => {
  if (minutes < 90) return { emoji: '☕', text: translate("That's {count} coffee breaks", { count: Math.floor(minutes / 15) }) };
  if (minutes < 16 * 60) {
    return { emoji: '⚽', text: translate("That's like watching {count} full football matches", { count: Math.floor(minutes / 90) }) };
  }
  return { emoji: '💼', text: translate("That's {count} full work days you didn't spend captioning", { count: Math.floor(minutes / 480) }) };
};

// Counts up from 0 to an integer target; non-numeric values render as-is.
const useCountUp = (value) => {
  const target = /^\d+$/.test(value) ? Number(value) : null;
  const [current, setCurrent] = useState(target === null ? value : '0');

  useEffect(() => {
    if (target === null) {
      setCurrent(value);
      return undefined;
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || target === 0) {
      setCurrent(value);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const duration = 1100;
    const delay = 200; // let the pop land first
    const tick = (now) => {
      const t = Math.min(Math.max(now - start - delay, 0) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setCurrent(String(Math.round(target * eased)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, target]);

  return current;
};

// Hourglass with a spark: time, magically saved.
const HourglassSparkIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 3h10M6 21h10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path
      d="M7.5 3v2.6c0 1.3.6 2.5 1.7 3.2L11 10l1.8-1.2c1.1-.7 1.7-1.9 1.7-3.2V3M7.5 21v-2.6c0-1.3.6-2.5 1.7-3.2L11 14l1.8 1.2c1.1.7 1.7 1.9 1.7 3.2V21"
      stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"
    />
    <path d="M9.2 19.2h3.6L11 17.6z" fill="currentColor" />
    <path d="M19.5 7.2l.7 1.6 1.6.7-1.6.7-.7 1.6-.7-1.6-1.6-.7 1.6-.7z" fill="currentColor" />
    <path d="M18.6 2.4l.4.9.9.4-.9.4-.4.9-.4-.9-.9-.4.9-.4z" fill="currentColor" />
  </svg>
);

/**
 * "Time saved with ArabCap": compact card used in the desktop sidebar and
 * at the top of the mobile dashboard. Renders nothing until the account has
 * a meaningful amount of time saved; pops in on mount.
 */
const TimeSavedCard = ({ videoSeconds = 0, videos = 0 }) => {
  const { t } = useI18n(); // re-render on language change
  const savedMinutes = savedMinutesFor(videoSeconds);
  const { value, unit } = formatTimeSaved(savedMinutes);
  const shownValue = useCountUp(value);

  if (!isTimeSavedSignificant({ videoSeconds, videos })) return null;

  const equivalent = timeSavedEquivalent(savedMinutes);
  const note = t('{count} videos · vs. captioning by hand (~{rate} min per video minute)', { count: videos, rate: MANUAL_MINUTES_PER_VIDEO_MINUTE });

  return (
    <div
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#f73140] to-[#d62331] px-4 py-3.5 text-white shadow-lg shadow-[#f73140]/25 animate-pop motion-reduce:animate-none"
      title={note}
    >
      {/* Brand decoration: concentric rings + faint hourglass watermark */}
      <div className="absolute -top-12 -end-12 w-32 h-32 rounded-full border border-white/15 pointer-events-none" />
      <div className="absolute -top-20 -end-20 w-48 h-48 rounded-full border border-white/10 pointer-events-none" />
      <div className="absolute -bottom-4 -end-2 text-white/[0.08] pointer-events-none">
        <HourglassSparkIcon size={76} />
      </div>

      <div className="relative z-10">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-white text-[#f73140] flex items-center justify-center shrink-0 shadow-sm">
            <HourglassSparkIcon size={15} />
          </span>
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-white/90">
            {t('Time saved with ArabCap')}
          </span>
        </div>
        <p className="mt-2 flex items-baseline gap-1.5 leading-none tabular-nums">
          <span className="text-[34px] font-extrabold tracking-tight">{shownValue}</span>
          {unit && <span className="text-sm font-bold text-white/90">{unit}</span>}
        </p>
        <p className="mt-1.5 text-[12px] font-semibold leading-snug text-white">
          {equivalent.emoji} {equivalent.text}
        </p>
      </div>
    </div>
  );
};

export default TimeSavedCard;
